import React, { useEffect, useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { User } from "lucide-react";

export default function PerfilProdutor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [tipoPessoa, setTipoPessoa] = useState("pf");
  const [cpf, setCpf] = useState("");
  const [rg, setRg] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [ie, setIe] = useState("");
  const [im, setIm] = useState("");
  const [telefone, setTelefone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [cep, setCep] = useState("");
  const [pais, setPais] = useState("BR");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [contatoNome, setContatoNome] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentTermos, setConsentTermos] = useState(false);
  const [consentPrivacidade, setConsentPrivacidade] = useState(false);
  const [consentInitial, setConsentInitial] = useState({ marketing: false, termos: false, privacidade: false });
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [consentInfo, setConsentInfo] = useState("");
  const [lgpdTipo, setLgpdTipo] = useState("acesso");
  const [lgpdDetails, setLgpdDetails] = useState("");
  const [lgpdSaving, setLgpdSaving] = useState(false);
  const [lgpdError, setLgpdError] = useState("");
  const [lgpdInfo, setLgpdInfo] = useState("");
  const [lgpdRequests, setLgpdRequests] = useState([]);

  useEffect(() => {
    const load = async () => {
      setError("");
      setInfo("");
      setLoading(true);
      try {
        const u = await agrogestao.auth.me();
        const em = u?.email || "";
        const defaultName = (u && (u.user_metadata?.full_name || u.full_name)) || "";
        setEmail(em);
        const { data } = await agrogestao.client.from('users').select('*').ilike('email', em).maybeSingle();
        if (data) {
          setFullName(data.full_name || defaultName);
          setTipoPessoa(data.tipo_pessoa || "pf");
          setCpf(data.cpf || "");
          setRg(data.rg || "");
          setCnpj(data.cnpj || "");
          setIe(data.inscricao_estadual || "");
          setIm(data.inscricao_municipal || "");
          setTelefone(data.telefone || "");
          setWhatsapp(data.whatsapp || "");
          setLogradouro(data.endereco_logradouro || "");
          setNumero(data.endereco_numero || "");
          setComplemento(data.endereco_complemento || "");
          setBairro(data.endereco_bairro || "");
          setCidade(data.endereco_cidade || "");
          setEstado(data.endereco_estado || "");
          setCep(data.endereco_cep || "");
          setPais(data.pais || "BR");
          setRazaoSocial(data.razao_social || "");
          setNomeFantasia(data.nome_fantasia || "");
          setContatoNome(data.contato_nome || "");
          setDataNascimento(data.data_nascimento ? String(data.data_nascimento).slice(0,10) : "");
        } else {
          if (defaultName) setFullName(defaultName);
        }
        try {
          const { data: consData } = await agrogestao.client
            .from('consentimentos')
            .select('scope, granted_at, revoked_at, created_at')
            .eq('user_email', em);
          const hasActive = (scope) => {
            return (consData || []).some(c => c.scope === scope && !c.revoked_at);
          };
          const m = hasActive('marketing');
          const t = hasActive('termos');
          const p = hasActive('privacidade');
          setConsentMarketing(!!m);
          setConsentTermos(!!t);
          setConsentPrivacidade(!!p);
          setConsentInitial({ marketing: !!m, termos: !!t, privacidade: !!p });
        } catch {}
        try {
          const { data: reqs } = await agrogestao.client
            .from('lgpd_solicitacoes')
            .select('*')
            .eq('user_email', em)
            .order('created_at', { ascending: false })
            .limit(50);
          setLgpdRequests(reqs || []);
        } catch {}
      } catch (err) {
        setError("Erro ao carregar perfil");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sanitizeDigits = (s) => (s || "").replace(/\D/g, "");

  const maskCPF = (v) => {
    const d = sanitizeDigits(v).slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
  };

  const maskCNPJ = (v) => {
    const d = sanitizeDigits(v).slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
    return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
  };

  const maskCEP = (v) => {
    const d = sanitizeDigits(v).slice(0, 8);
    if (d.length <= 5) return d;
    return `${d.slice(0,5)}-${d.slice(5,8)}`;
  };

  const maskPhone = (v) => {
    const d = sanitizeDigits(v).slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6,10)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`;
  };

  const handleSave = async () => {
    setError("");
    setInfo("");
    setSaving(true);
    try {
      const cpfDigits = sanitizeDigits(cpf);
      const cnpjDigits = sanitizeDigits(cnpj);
      const telefoneDigits = sanitizeDigits(telefone);
      const whatsappDigits = sanitizeDigits(whatsapp);
      const cepDigits = sanitizeDigits(cep);
      const uf = String(estado || "").trim().toUpperCase().slice(0, 2);
      if (tipoPessoa === 'pf' && cpfDigits && cpfDigits.length !== 11) {
        throw new Error('CPF deve ter 11 dígitos');
      }
      if (tipoPessoa === 'pj' && cnpjDigits && cnpjDigits.length !== 14) {
        throw new Error('CNPJ deve ter 14 dígitos');
      }
      if (cepDigits && !(cepDigits.length === 8)) {
        throw new Error('CEP deve ter 8 dígitos');
      }
      if (uf && uf.length !== 2) {
        throw new Error('UF deve ter 2 letras');
      }
      const payload = {
        full_name: fullName,
        tipo_pessoa: tipoPessoa,
        cpf: cpfDigits || null,
        rg: rg,
        cnpj: cnpjDigits || null,
        inscricao_estadual: ie,
        inscricao_municipal: im,
        telefone: telefoneDigits || null,
        whatsapp: whatsappDigits || null,
        endereco_logradouro: logradouro,
        endereco_numero: numero,
        endereco_complemento: complemento,
        endereco_bairro: bairro,
        endereco_cidade: cidade,
        endereco_estado: uf || null,
        endereco_cep: cepDigits ? maskCEP(cepDigits) : null,
        pais,
        razao_social: razaoSocial,
        nome_fantasia: nomeFantasia,
        contato_nome: contatoNome,
        data_nascimento: dataNascimento || null
      };
      const { data: existing } = await agrogestao.client.from('users').select('id,email').eq('email', email).maybeSingle();
      if (existing && existing.id) {
        const { error: updErr } = await agrogestao.client.from('users').update(payload).eq('email', email);
        if (updErr) throw updErr;
      } else {
        const { error: insErr } = await agrogestao.client.from('users').insert({ email, ...payload }).select().single();
        if (insErr) throw insErr;
      }
      setInfo('Dados atualizados com sucesso');
      const digits = (s) => String(s || '').replace(/\D/g, '');
      const uf2 = String(estado || '').trim().toUpperCase().slice(0, 2);
      const okPf = (
        tipoPessoa === 'pf' &&
        !!fullName &&
        digits(cpf).length === 11 &&
        !!telefoneDigits &&
        !!cidade &&
        uf2.length === 2 &&
        cepDigits.length === 8
      );
      const okPj = (
        tipoPessoa === 'pj' &&
        digits(cnpj).length === 14 &&
        (!!razaoSocial || !!fullName) &&
        !!telefoneDigits &&
        !!cidade &&
        uf2.length === 2 &&
        cepDigits.length === 8
      );
      if (okPf || okPj) {
        try { window.location.href = '/Propriedades'; } catch {}
      }
    } catch (err) {
      setError(err?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConsents = async () => {
    setConsentError("");
    setConsentInfo("");
    setConsentSaving(true);
    try {
      const scopes = [
        { key: 'marketing', value: consentMarketing },
        { key: 'termos', value: consentTermos },
        { key: 'privacidade', value: consentPrivacidade }
      ];
      for (const s of scopes) {
        const current = consentInitial[s.key];
        const desired = s.value;
        if (desired === current) continue;
        if (desired) {
          const { error: insErr } = await agrogestao.client.from('consentimentos').insert({
            user_email: email,
            scope: s.key,
            granted_at: new Date().toISOString()
          });
          if (insErr) throw insErr;
        } else {
          const { error: updErr } = await agrogestao.client
            .from('consentimentos')
            .update({ revoked_at: new Date().toISOString() })
            .eq('user_email', email)
            .eq('scope', s.key)
            .is('revoked_at', null);
          if (updErr) throw updErr;
        }
      }
      setConsentInitial({ marketing: consentMarketing, termos: consentTermos, privacidade: consentPrivacidade });
      setConsentInfo('Consentimentos atualizados');
    } catch (err) {
      setConsentError('Erro ao atualizar consentimentos');
    } finally {
      setConsentSaving(false);
    }
  };

  const handleCreateLgpdRequest = async () => {
    setLgpdError("");
    setLgpdInfo("");
    setLgpdSaving(true);
    try {
      const payload = {
        user_email: email,
        tipo: lgpdTipo,
        details: lgpdDetails ? { texto: lgpdDetails } : null
      };
      const { data: inserted, error: insErr } = await agrogestao.client
        .from('lgpd_solicitacoes')
        .insert(payload)
        .select()
        .single();
      if (insErr) throw insErr;
      setLgpdInfo('Solicitação registrada');
      setLgpdDetails("");
      setLgpdRequests([inserted, ...(lgpdRequests || [])]);
    } catch (err) {
      setLgpdError('Erro ao registrar solicitação');
    } finally {
      setLgpdSaving(false);
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <User className="w-8 h-8 text-[#1B4332]" />
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Perfil do Produtor</h1>
          <p className="text-stone-600">Atualize seus dados fiscais e de contato</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input value={email} disabled />
            </div>
            <div className="space-y-2">
              <Label>Nome completo</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Tipo de pessoa</Label>
              <Select value={tipoPessoa} onValueChange={setTipoPessoa}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pf">Pessoa Física</SelectItem>
                  <SelectItem value="pj">Pessoa Jurídica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tipoPessoa === 'pf' && (
              <div className="space-y-2">
                <Label>CPF</Label>
                <Input value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} placeholder="000.000.000-00" />
              </div>
            )}
            {tipoPessoa === 'pf' && (
              <div className="space-y-2">
                <Label>RG</Label>
                <Input value={rg} onChange={(e) => setRg(e.target.value)} />
              </div>
            )}
            {tipoPessoa === 'pj' && (
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input value={cnpj} onChange={(e) => setCnpj(maskCNPJ(e.target.value))} placeholder="00.000.000/0000-00" />
              </div>
            )}
            {tipoPessoa === 'pj' && (
              <div className="space-y-2">
                <Label>Inscrição Estadual</Label>
                <Input value={ie} onChange={(e) => setIe(e.target.value)} />
              </div>
            )}
            {tipoPessoa === 'pj' && (
              <div className="space-y-2">
                <Label>Inscrição Municipal</Label>
                <Input value={im} onChange={(e) => setIm(e.target.value)} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input value={telefone} onChange={(e) => setTelefone(maskPhone(e.target.value))} placeholder="(00) 0000-0000" />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp</Label>
              <Input value={whatsapp} onChange={(e) => setWhatsapp(maskPhone(e.target.value))} placeholder="(00) 00000-0000" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Logradouro</Label>
              <Input value={logradouro} onChange={(e) => setLogradouro(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Número</Label>
              <Input value={numero} onChange={(e) => setNumero(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Complemento</Label>
              <Input value={complemento} onChange={(e) => setComplemento(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Bairro</Label>
              <Input value={bairro} onChange={(e) => setBairro(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Cidade</Label>
              <Input value={cidade} onChange={(e) => setCidade(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Estado (UF)</Label>
              <Input value={estado} onChange={(e) => setEstado(String(e.target.value || '').toUpperCase().slice(0,2))} placeholder="UF" />
            </div>
            <div className="space-y-2">
              <Label>CEP</Label>
              <Input value={cep} onChange={(e) => setCep(maskCEP(e.target.value))} placeholder="00000-000" />
            </div>
            <div className="space-y-2">
              <Label>País</Label>
              <Input value={pais} onChange={(e) => setPais(e.target.value)} />
            </div>
            {tipoPessoa === 'pj' && (
              <div className="space-y-2">
                <Label>Razão Social</Label>
                <Input value={razaoSocial} onChange={(e) => setRazaoSocial(e.target.value)} />
              </div>
            )}
            {tipoPessoa === 'pj' && (
              <div className="space-y-2">
                <Label>Nome Fantasia</Label>
                <Input value={nomeFantasia} onChange={(e) => setNomeFantasia(e.target.value)} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Contato responsável</Label>
              <Input value={contatoNome} onChange={(e) => setContatoNome(e.target.value)} />
            </div>
            {tipoPessoa === 'pf' && (
              <div className="space-y-2">
                <Label>Data de nascimento</Label>
                <Input type="date" value={dataNascimento} onChange={(e) => setDataNascimento(e.target.value)} />
              </div>
            )}
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {info && <div className="text-green-700 text-sm">{info}</div>}

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-stone-800">Privacidade e LGPD</h2>
            <p className="text-stone-600 text-sm">Gerencie seus consentimentos e solicitações</p>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox checked={consentMarketing} onCheckedChange={(v) => setConsentMarketing(!!v)} />
              <div>
                <p className="font-medium">Receber comunicações de marketing</p>
                <p className="text-stone-600 text-sm">Novidades, ofertas e conteúdos</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox checked={consentTermos} onCheckedChange={(v) => setConsentTermos(!!v)} />
              <div>
                <p className="font-medium">Aceitar termos de uso</p>
                <p className="text-stone-600 text-sm">Confirmação do aceite vigente</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox checked={consentPrivacidade} onCheckedChange={(v) => setConsentPrivacidade(!!v)} />
              <div>
                <p className="font-medium">Aceitar política de privacidade</p>
                <p className="text-stone-600 text-sm">Tratamento de dados pessoais</p>
              </div>
            </div>
            {consentError && <div className="text-red-600 text-sm">{consentError}</div>}
            {consentInfo && <div className="text-green-700 text-sm">{consentInfo}</div>}
            <div className="flex gap-2">
              <Button onClick={handleSaveConsents} disabled={consentSaving} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
                {consentSaving ? 'Salvando...' : 'Salvar consentimentos'}
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-stone-800">Solicitações LGPD</h3>
              <p className="text-stone-600 text-sm">Registre pedidos de acesso, retificação ou exclusão</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de solicitação</Label>
                <Select value={lgpdTipo} onValueChange={setLgpdTipo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acesso">Acesso</SelectItem>
                    <SelectItem value="retificacao">Retificação</SelectItem>
                    <SelectItem value="exclusao">Exclusão</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Detalhes</Label>
                <Textarea value={lgpdDetails} onChange={(e) => setLgpdDetails(e.target.value)} placeholder="Descreva sua solicitação" />
              </div>
            </div>
            {lgpdError && <div className="text-red-600 text-sm">{lgpdError}</div>}
            {lgpdInfo && <div className="text-green-700 text-sm">{lgpdInfo}</div>}
            <div className="flex gap-2">
              <Button onClick={handleCreateLgpdRequest} disabled={lgpdSaving} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
                {lgpdSaving ? 'Enviando...' : 'Registrar solicitação'}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-lg font-semibold text-stone-800">Minhas solicitações</h4>
            <div className="space-y-2">
              {(lgpdRequests || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                  <div className="text-sm">
                    <p className="font-medium">{r.tipo}</p>
                    <p className="text-stone-600">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</p>
                  </div>
                  <div className="text-sm font-semibold">{r.status}</div>
                </div>
              ))}
              {(!lgpdRequests || lgpdRequests.length === 0) && (
                <div className="text-stone-600 text-sm">Nenhuma solicitação registrada</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
